'use client';

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Typography } from '../../../components';
import { useTranslations } from '../../../contexts/translation.context';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';
import { FiltersSection, AccountsTable, AccountContentModal } from './components';
import { AccountsFilters, RevshareAccount, StatusCounts } from './types';
import { DEFAULT_FILTERS, DEFAULT_PAGE_SIZE, SEARCH_DEBOUNCE_DELAY } from './constants';
import partnerRevshareService, {
  CompaniesModelData,
} from '../../../api/partner/partnerRevshareService';

// Map work_content category keys to display labels
const getCategoryLabel = (category: string): string => {
  const labels: Record<string, string> = {
    nonude: 'No nude',
    tt: 'TikTok',
    lingerie: 'Lingerie',
    lite_nude: 'Lite nude',
    full_nude: 'Full nude',
  };
  return labels[category] || category;
};

// Transform API response to RevshareAccount format
const transformAccountData = (account: CompaniesModelData): RevshareAccount => ({
  _id: account._id,
  of_user_id: account.of_user_id,
  of_user_name: account.of_user_name,
  name: account.name,
  avatar: account.avatar,
  added_at: account.created_at || new Date().toISOString(),
  status: account.status,
  type: account.type || 'free',
  // Extract stats from of_data
  subscribers: account.of_data?.subscribers || 0,
  likes: account.of_data?.favorited || 0,
  posts: account.of_data?.posts || 0,
  overall_popularity_percent: account.of_data?.overall_popularity_percent || 0,
  // Campaign types
  company_types: account.company_types || [],
  campaigns_count: account.companies_count || 0,
  // These are not in API yet - using defaults
  income: 0,
  pending_income: 0,
  mutual_subscribers: 0,
  // Content
  public_content: account.public_content,
  work_content: account.work_content,
});

const RevshareAccounts: React.FC = () => {
  const { t } = useTranslations();

  // Breadcrumbs
  const breadcrumbs = useMemo(
    () => [
      { label: t('sidebar.dashboard') || 'Главная', href: '/partner/dashboard' },
      { label: 'Revshare', href: '/partner/revshare/offers' },
      { label: t('partner.partnerAccounts.title') || 'Аккаунты', isActive: true },
    ],
    [t]
  );
  usePageBreadcrumbs(breadcrumbs);

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [accounts, setAccounts] = useState<RevshareAccount[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Filters
  const [filters, setFilters] = useState<AccountsFilters>(DEFAULT_FILTERS);
  const [localSearch, setLocalSearch] = useState('');

  // Modal state
  const [isContentModalOpen, setIsContentModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<RevshareAccount | null>(null);

  // Check if filters are active
  const hasActiveFilters = useMemo(() => {
    return filters.search !== '' || filters.status !== '' || filters.source !== '';
  }, [filters]);

  // Calculate all status counts
  const statusCounts: StatusCounts = useMemo(() => {
    return {
      total: accounts.length,
      active: accounts.filter((acc) => acc.status === 'active').length,
      deactivated: accounts.filter((acc) => acc.status === 'deactivated').length,
      moderation: accounts.filter((acc) => acc.status === 'moderation').length,
      wait_media: accounts.filter((acc) => acc.status === 'wait_media').length,
      verified: accounts.filter((acc) => acc.status === 'verified').length,
    };
  }, [accounts]);

  // Ref to track current request ID (to handle StrictMode double-invocation)
  const requestIdRef = useRef(0);
  // Ref to skip debounce on initial mount
  const isInitialMount = useRef(true);

  // Fetch accounts from API - directly in useEffect to avoid double render
  useEffect(() => {
    // Increment request ID to invalidate any in-flight requests
    const currentRequestId = ++requestIdRef.current;

    setIsLoading(true);

    const fetchData = async () => {
      try {
        const params: {
          page: number;
          page_size: number;
          search?: string;
          status?: 'verified' | 'wait_media' | 'moderation' | 'active' | 'deactivated';
          company_type?: 'cpl' | 'refshare';
        } = {
          page: currentPage,
          page_size: pageSize,
        };

        if (filters.search) {
          params.search = filters.search;
        }

        if (filters.status) {
          params.status = filters.status as
            | 'verified'
            | 'wait_media'
            | 'moderation'
            | 'active'
            | 'deactivated';
        }

        if (filters.source) {
          params.company_type = filters.source as 'cpl' | 'refshare';
        }

        const response = await partnerRevshareService.getCompaniesModels(params);

        // Only update state if this is still the current request
        if (requestIdRef.current !== currentRequestId) {
          return;
        }

        // Transform API response to RevshareAccount format
        const transformedAccounts = response.objects.map(transformAccountData);

        setAccounts(transformedAccounts);
        setTotalCount(response.count);
      } catch (error: any) {
        // Only handle error if this is still the current request
        if (requestIdRef.current !== currentRequestId) {
          return;
        }
        console.error('Error fetching accounts:', error);
        setAccounts([]);
        setTotalCount(0);
      } finally {
        // Only update loading state if this is still the current request
        if (requestIdRef.current === currentRequestId) {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, [currentPage, pageSize, filters]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Debounce search - only update if value actually changed, skip on initial mount
  useEffect(() => {
    // Skip debounce on initial mount to prevent double render
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const timeoutId = setTimeout(() => {
      setFilters((prev) => {
        if (prev.search !== localSearch) {
          return { ...prev, search: localSearch };
        }
        return prev;
      });
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timeoutId);
  }, [localSearch]);

  const handleFilterChange = useCallback((key: keyof AccountsFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setLocalSearch('');
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleShowContent = useCallback((account: RevshareAccount) => {
    setSelectedAccount(account);
    setIsContentModalOpen(true);
  }, []);

  const handleCloseContentModal = useCallback(() => {
    setIsContentModalOpen(false);
    setSelectedAccount(null);
  }, []);

  return (
    <div className="w-full px-4 py-6 md:p-6">
      {/* Header */}
      <div className="mb-6 ">
        <Typography variant="h4" className="mb-2">
          {t('partner.partnerAccounts.title')}
        </Typography>
      </div>

      {/* Filters */}
      <FiltersSection
        filters={filters}
        localSearch={localSearch}
        isLoading={isLoading}
        hasActiveFilters={hasActiveFilters}
        statusCounts={statusCounts}
        onSearchChange={setLocalSearch}
        onFilterChange={handleFilterChange}
        onResetFilters={handleResetFilters}
      />

      {/* Table */}
      <AccountsTable
        accounts={accounts}
        isLoading={isLoading}
        totalCount={totalCount}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onShowContent={handleShowContent}
      />

      {/* Content Modal */}
      {selectedAccount && (
        <AccountContentModal
          isOpen={isContentModalOpen}
          onClose={handleCloseContentModal}
          modelId={selectedAccount.of_user_id}
          mediaUrls={selectedAccount.public_content || []}
          contentLinks={
            selectedAccount.work_content
              ? Object.entries(selectedAccount.work_content)
                  .filter(([_, url]) => url && url.trim() !== '')
                  .map(([category, url]) => ({
                    category: getCategoryLabel(category),
                    url: url as string,
                  }))
              : []
          }
        />
      )}
    </div>
  );
};

export default RevshareAccounts;
